/** Keyboard, mouse and touch state converted into per-tick intents. */
export interface TickInput {
  mx: number;
  my: number;
  fire: boolean;
  skills: number;
  dash: boolean;
  use: number;
  pick: number;
  interact: boolean;
  aimScreenX: number;
  aimScreenY: number;
  /** aim direction from the right stick; 0,0 means "no explicit aim" */
  aimDx: number;
  aimDy: number;
}

export const KEYS = {
  up: ['KeyW', 'ArrowUp'],
  down: ['KeyS', 'ArrowDown'],
  left: ['KeyA', 'ArrowLeft'],
  right: ['KeyD', 'ArrowRight'],
  dash: ['Space', 'ShiftLeft', 'ShiftRight'],
  skills: ['KeyQ', 'KeyE', 'KeyR', 'KeyF'],
  cons: ['Digit1', 'Digit2', 'Digit3', 'Digit4'],
  picks: ['KeyZ', 'KeyX', 'KeyC', 'KeyV', 'KeyB'],
  interact: ['KeyG'],
  build: ['Tab'],
  chat: ['Enter'],
  menu: ['Escape'],
  map: ['KeyM'],
  autofire: ['KeyI'],
  leave: ['KeyL'],
};

export const KEY_LABELS = { skills: ['Q', 'E', 'R', 'F'], cons: ['1', '2', '3', '4'], picks: ['Z', 'X', 'C', 'V', 'B'], dash: 'Space', interact: 'G', build: 'Tab', leave: 'L' };

/** How far a finger travels for full tilt. */
const STICK_RADIUS = 54;
/** Below this the aim stick only turns the hero, it does not fire. */
const AIM_DEADZONE = 0.25;

export interface StickState {
  /** where the finger landed */
  ox: number;
  oy: number;
  /** tilt, each -1..1 */
  dx: number;
  dy: number;
}

function typing(): boolean {
  const el = document.activeElement as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || el.isContentEditable;
}

/** Touches that land on the interface itself must not steer the hero. */
function onUi(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  return !!el?.closest?.('#ui .clickable, #ui button, #ui input, #ui a, #ui .panel, #ui .dcard, #ui .touch-btn');
}

export class Input {
  private keys = new Set<string>();
  mouseX = 0;
  mouseY = 0;
  private lmb = false;
  private rmb = false;
  private skillsPressed = 0;
  private dashPressed = false;
  private usePressed = -1;
  private pickPressed = -1;
  private interactPressed = false;
  autoFire = false;
  /** phones steer with two sticks instead of a mouse */
  touch = false;
  private move: (StickState & { id: number }) | null = null;
  private aim: (StickState & { id: number }) | null = null;
  onToggle: (what: 'build' | 'chat' | 'menu' | 'map' | 'autofire' | 'leave') => void = () => {};
  onStick: (kind: 'move' | 'aim', s: StickState | null) => void = () => {};
  onPickClick: ((idx: number) => void) | null = null;
  private target: HTMLElement | null = null;

  private onKeyDown = (e: KeyboardEvent) => {
    if (typing()) {
      if (e.code === 'Escape') this.onToggle('chat');
      return;
    }
    if (KEYS.build.includes(e.code)) {
      e.preventDefault();
      this.onToggle('build');
      return;
    }
    if (KEYS.chat.includes(e.code)) {
      e.preventDefault();
      this.onToggle('chat');
      return;
    }
    if (KEYS.menu.includes(e.code)) {
      this.onToggle('menu');
      return;
    }
    if (KEYS.map.includes(e.code)) {
      this.onToggle('map');
      return;
    }
    if (KEYS.autofire.includes(e.code)) {
      this.autoFire = !this.autoFire;
      this.onToggle('autofire');
      return;
    }
    if (KEYS.leave.includes(e.code)) {
      this.onToggle('leave');
      return;
    }
    if (e.repeat) return;
    this.keys.add(e.code);
    const si = KEYS.skills.indexOf(e.code);
    if (si >= 0) this.skillsPressed |= 1 << si;
    if (KEYS.dash.includes(e.code)) {
      this.dashPressed = true;
      e.preventDefault();
    }
    const ci = KEYS.cons.indexOf(e.code);
    if (ci >= 0) this.usePressed = ci;
    const pi = KEYS.picks.indexOf(e.code);
    if (pi >= 0) this.pickPressed = pi;
    if (KEYS.interact.includes(e.code)) this.interactPressed = true;
  };

  private onKeyUp = (e: KeyboardEvent) => {
    this.keys.delete(e.code);
  };

  private onMouseMove = (e: MouseEvent) => {
    this.mouseX = e.clientX;
    this.mouseY = e.clientY;
  };

  private onMouseDown = (e: MouseEvent) => {
    if (onUi(e.target)) return;
    if (e.button === 0) this.lmb = true;
    if (e.button === 2) {
      this.rmb = true;
      this.skillsPressed |= 1;
    }
  };

  private onMouseUp = (e: MouseEvent) => {
    if (e.button === 0) this.lmb = false;
    if (e.button === 2) this.rmb = false;
  };

  private onBlur = () => {
    this.keys.clear();
    this.lmb = false;
    this.rmb = false;
    this.clearSticks();
  };

  // ---------------------------------------------------------------- touch sticks

  private onPointerDown = (e: PointerEvent) => {
    if (!this.touch || e.pointerType === 'mouse' || onUi(e.target)) return;
    const s = { id: e.pointerId, ox: e.clientX, oy: e.clientY, dx: 0, dy: 0 };
    // the left half of the screen moves and the right half aims; a busy side hands the finger over
    const left = e.clientX < window.innerWidth * 0.5;
    const takeMove = left ? !this.move : !!this.aim && !this.move;
    if (takeMove) {
      this.move = s;
      this.onStick('move', s);
      return;
    }
    if (!this.aim) {
      this.aim = s;
      this.onStick('aim', s);
    }
  };

  private onPointerMove = (e: PointerEvent) => {
    if (!this.touch) return;
    const s = this.move?.id === e.pointerId ? this.move : this.aim?.id === e.pointerId ? this.aim : null;
    if (!s) return;
    const dx = (e.clientX - s.ox) / STICK_RADIUS;
    const dy = (e.clientY - s.oy) / STICK_RADIUS;
    const len = Math.hypot(dx, dy);
    s.dx = len > 1 ? dx / len : dx;
    s.dy = len > 1 ? dy / len : dy;
    this.onStick(s === this.move ? 'move' : 'aim', { ox: s.ox, oy: s.oy, dx: s.dx, dy: s.dy });
  };

  private onPointerUp = (e: PointerEvent) => {
    if (this.move?.id === e.pointerId) {
      this.move = null;
      this.onStick('move', null);
    }
    if (this.aim?.id === e.pointerId) {
      this.aim = null;
      this.onStick('aim', null);
    }
  };

  clearSticks(): void {
    if (this.move) {
      this.move = null;
      this.onStick('move', null);
    }
    if (this.aim) {
      this.aim = null;
      this.onStick('aim', null);
    }
  }

  setTouch(on: boolean): void {
    if (this.touch === on) return;
    this.touch = on;
    this.clearSticks();
    // fingers cannot hold an attack button, so touch play fires by itself
    if (on) this.autoFire = true;
  }

  attach(target: HTMLElement): void {
    this.target = target;
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('mousemove', this.onMouseMove);
    window.addEventListener('mousedown', this.onMouseDown);
    window.addEventListener('mouseup', this.onMouseUp);
    window.addEventListener('blur', this.onBlur);
    window.addEventListener('pointerdown', this.onPointerDown);
    window.addEventListener('pointermove', this.onPointerMove);
    window.addEventListener('pointerup', this.onPointerUp);
    window.addEventListener('pointercancel', this.onPointerUp);
    target.addEventListener('contextmenu', this.prevent);
  }

  private prevent = (e: Event) => e.preventDefault();

  detach(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('mousemove', this.onMouseMove);
    window.removeEventListener('mousedown', this.onMouseDown);
    window.removeEventListener('mouseup', this.onMouseUp);
    window.removeEventListener('blur', this.onBlur);
    window.removeEventListener('pointerdown', this.onPointerDown);
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.onPointerUp);
    window.removeEventListener('pointercancel', this.onPointerUp);
    this.target?.removeEventListener('contextmenu', this.prevent);
    this.clearSticks();
  }

  pressPick(idx: number): void {
    this.pickPressed = idx;
  }

  pressUse(slot: number): void {
    this.usePressed = slot;
  }

  pressSkill(i: number): void {
    this.skillsPressed |= 1 << i;
  }

  pressDash(): void {
    this.dashPressed = true;
  }

  pressInteract(): void {
    this.interactPressed = true;
  }

  isDown(code: string): boolean {
    return this.keys.has(code);
  }

  /** Reads and resets one-shot intents. */
  consume(): TickInput {
    const has = (list: string[]) => list.some((k) => this.keys.has(k));
    let mx = 0;
    let my = 0;
    if (!typing()) {
      if (has(KEYS.left)) mx -= 1;
      if (has(KEYS.right)) mx += 1;
      if (has(KEYS.up)) my -= 1;
      if (has(KEYS.down)) my += 1;
    }
    if (this.move) {
      mx = this.move.dx;
      my = this.move.dy;
    }
    const aimLen = this.aim ? Math.hypot(this.aim.dx, this.aim.dy) : 0;
    const aiming = !!this.aim && aimLen > AIM_DEADZONE;
    const out: TickInput = {
      mx,
      my,
      fire: this.lmb || this.autoFire || aiming,
      skills: this.skillsPressed,
      dash: this.dashPressed,
      use: this.usePressed,
      pick: this.pickPressed,
      interact: this.interactPressed,
      aimScreenX: this.mouseX,
      aimScreenY: this.mouseY,
      aimDx: aiming ? this.aim!.dx / aimLen : 0,
      aimDy: aiming ? this.aim!.dy / aimLen : 0,
    };
    this.skillsPressed = 0;
    this.dashPressed = false;
    this.usePressed = -1;
    this.pickPressed = -1;
    this.interactPressed = false;
    return out;
  }
}
